import React, { useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Truck, CheckCircle2, ChevronUp, ChevronDown } from "lucide-react";
import { STAGE_PATHS, parseDateDash, fmtDateShort, fmtMoney } from "./utils";
import { ClientsContext, ClientGroupsContext } from "@/App";

const ACCENT = "#10b981";

/**
 * MonthDispatchLightbox — drill-down table of items dispatched in a given month.
 *
 * Props:
 *   open, onOpenChange  — Dialog open state
 *   items               — full items array (same as MonthlyDispatchesChart receives)
 *   monthName           — e.g. "February"
 *   year                — full year, e.g. 2026
 *   month               — 0-indexed month (JS Date convention)
 */
export default function MonthDispatchLightbox({
  open, onOpenChange, items, monthName, year, month,
}) {
  const navigate = useNavigate();
  const [sortCol, setSortCol] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const clientsCtx = useContext(ClientsContext);
  const clientGroupsCtx = useContext(ClientGroupsContext);
  const clients = clientsCtx?.clients || [];
  const clientGroups = clientGroupsCtx?.clientGroups || [];

  const clientLogoMap = useMemo(() => {
    const map = {};
    if (!clients || clients.length === 0) return map;

    clients.forEach(c => {
      if (!c.name) return;
      if (c.logo_svg && c.logo_svg.trim()) {
        map[c.name] = c.logo_svg;
        return;
      }
      if (!clientGroups || clientGroups.length === 0) return;
      const group = clientGroups.find(g => g.client_ids && g.client_ids.includes(c.id));
      if (group && group.logo_svg && group.logo_svg.trim()) {
        map[c.name] = group.logo_svg;
      }
    });
    return map;
  }, [clients, clientGroups]);

  // Filter by shipped_at (actual ship date) only — no fallback to dispatch_due_date.
  // Matches the "total this month" badge definition: items with an explicit
  // Shipped On date set within the target month.
  const rows = useMemo(() => {
    if (!open) return [];
    return items.filter(item => {
      if (!item.shipped_at) return false;
      const isShipped = item.stage === "Completed"
        || (item.stage === "Dispatch" && (item.dispatch_status === "Shipped" || item.dispatch_status === "Delivered"));
      if (!isShipped) return false;
      const d = new Date(item.shipped_at);
      return d.getFullYear() === year && d.getMonth() === month;
    });
  }, [open, items, year, month]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sortCol) {
        case "part":        return dir * (a.part_number || "").localeCompare(b.part_number || "");
        case "description": return dir * (a.item_description || "").localeCompare(b.item_description || "");
        case "client":      return dir * (a.client || "").localeCompare(b.client || "");
        case "po":          return dir * (a.po_number || "").localeCompare(b.po_number || "");
        case "qty":         return dir * ((Number(a.quantity) || 0) - (Number(b.quantity) || 0));
        case "value":       return dir * ((Number(a.value_usd) || 0) - (Number(b.value_usd) || 0));
        default: { // "date"
          const da = parseDateDash(a.shipped_at || a.dispatch_due_date || a.due_date);
          const db = parseDateDash(b.shipped_at || b.dispatch_due_date || b.due_date);
          return dir * ((da ? da.getTime() : 0) - (db ? db.getTime() : 0));
        }
      }
    });
  }, [rows, sortCol, sortDir]);

  const totalValue = rows.reduce((s, i) => s + (Number(i.value_usd) || 0), 0);

  const topClient = useMemo(() => {
    if (!rows.length) return { name: "—", value: 0, count: 0 };
    const map = {};
    rows.forEach(i => {
      const c = i.client || "Unknown";
      if (!map[c]) map[c] = { name: c, value: 0, count: 0 };
      map[c].value += Number(i.value_usd) || 0;
      map[c].count += 1;
    });
    return Object.values(map).sort((a, b) => b.value - a.value)[0];
  }, [rows]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  };

  const handleRowClick = (item) => {
    onOpenChange(false);
    const path = STAGE_PATHS[item.stage];
    if (path) navigate(path);
  };

  const renderClientCell = (clientName) => {
    if (!clientName) return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
    const logo = clientLogoMap[clientName];
    const initials = clientName.split(" ").slice(0, 2).map(w => w[0]).join("");
    return (
      <div className="flex items-center gap-1.5">
        <div className="shrink-0" style={{ width: "2px", height: "12px", borderRadius: "1px", background: "#9ca3af" }} />
        {logo ? (
          <span
            className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
            style={{ width: 13, height: 13 }}
            dangerouslySetInnerHTML={{ __html: logo }}
          />
        ) : (
          <span
            className="inline-flex items-center justify-center shrink-0 rounded-full text-[5px] font-bold leading-none"
            style={{ width: 13, height: 13, background: "#e5e7eb", color: "#6b7280" }}
          >
            {initials}
          </span>
        )}
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{clientName}</span>
      </div>
    );
  };

  const yy = String(year).slice(-2);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[1080px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="dispatch-lightbox"
      >
        {/* Accent rail */}
        <div className="h-[3px] w-full shrink-0" style={{ background: `linear-gradient(to right, ${ACCENT}, ${ACCENT}66)` }} />

        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[hsl(var(--border))] shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Truck className="w-5 h-5" style={{ color: ACCENT }} />
            <span>Dispatched in {monthName} &apos;{yy}</span>
            <span
              className="font-mono-data text-xs px-2 py-0.5 rounded border"
              style={{ color: ACCENT, borderColor: `${ACCENT}55`, backgroundColor: `${ACCENT}14` }}
            >
              {rows.length}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[hsl(var(--muted-foreground))]">
            Items completed this month or shipped/delivered from Dispatch. Tap any row to open it in its stage view.
          </DialogDescription>
        </DialogHeader>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3 px-6 py-4 border-b border-[hsl(var(--border))] shrink-0">
          <div className="rounded-lg border bg-[hsl(var(--muted))]/30 p-3">
            <p className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">Total Value</p>
            <p className="font-mono-data font-bold text-xl" style={{ color: ACCENT }}>
              {rows.length ? fmtMoney(totalValue) : "—"}
            </p>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">{rows.length} items</p>
          </div>
          <div className="rounded-lg border bg-[hsl(var(--muted))]/30 p-3">
            <p className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">Avg Value</p>
            <p className="font-mono-data font-bold text-xl" style={{ color: ACCENT }}>
              {rows.length ? fmtMoney(totalValue / rows.length) : "—"}
            </p>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">per line item</p>
          </div>
          <div className="rounded-lg border bg-[hsl(var(--muted))]/30 p-3">
            <p className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">Top Client</p>
            <p className="text-base font-bold truncate" style={{ color: ACCENT }}>
              {topClient.name}
            </p>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">
              {rows.length ? `${fmtMoney(topClient.value)} · ${topClient.count} items` : "—"}
            </p>
          </div>
        </div>

        {/* Table / empty state */}
        <div className="flex-1 overflow-y-auto" data-testid="dispatch-lightbox-table">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-[hsl(var(--muted-foreground))]">
              <CheckCircle2 className="w-10 h-10 mb-2 text-emerald-500 opacity-70" />
              <p className="text-sm font-medium">No items dispatched in {monthName} yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="sticky top-0 bg-[hsl(var(--card))] z-10 border-b border-[hsl(var(--border))]">
                  <ColHeader col="part" label="Part #" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="pl-6 text-left" />
                  <ColHeader col="description" label="Description" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="text-left" />
                  <ColHeader col="client" label="Client" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="text-left" />
                  <ColHeader col="po" label="PO · Line" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="text-left" />
                  <ColHeader col="qty" label="Qty" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="text-right" />
                  <ColHeader col="value" label="Value" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="text-right" />
                  <ColHeader col="date" label="Shipped / Completed On" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="pr-6 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]/60">
                {sorted.map(item => {
                  const dateRaw = item.shipped_at || item.dispatch_due_date || item.due_date;
                  return (
                    <tr
                      key={item.id}
                      onClick={() => handleRowClick(item)}
                      className="hover:bg-[hsl(var(--muted))]/50 cursor-pointer transition-colors"
                      data-testid={`dispatch-row-${item.id}`}
                    >
                      <td className="pl-6 pr-3 py-2.5 font-mono-data text-[11px] font-bold text-[hsl(var(--foreground))] whitespace-nowrap">
                        {item.part_number || "—"}
                      </td>
                      <td className="px-3 py-2.5 max-w-[200px]">
                        <span
                          className="block truncate text-[hsl(var(--foreground))]"
                          title={item.item_description}
                        >
                          {item.item_description}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                        {renderClientCell(item.client)}
                      </td>
                      <td className="px-3 py-2.5 font-mono-data text-[11px] whitespace-nowrap">
                        <span className="font-bold text-[hsl(var(--foreground))]">{item.po_number}</span>
                        {item.line_number && (
                          <span className="text-[hsl(var(--muted-foreground))] ml-1">· {item.line_number}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono-data text-[hsl(var(--foreground))]">
                        {item.quantity ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono-data font-bold" style={{ color: ACCENT }}>
                        {fmtMoney(item.value_usd || 0)}
                      </td>
                      <td className="pr-6 pl-3 py-2.5 text-right text-[hsl(var(--muted-foreground))] whitespace-nowrap font-mono-data text-[11px]">
                        {fmtDateShort(dateRaw)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-2.5 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 flex items-center justify-between shrink-0">
          <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
            Tap any row to open its stage view. · Total dispatched value: {fmtMoney(totalValue)}
          </span>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            data-testid="dispatch-lightbox-close"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ColHeader({ col, label, sortCol, sortDir, onSort, className = "" }) {
  const isActive = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest cursor-pointer select-none whitespace-nowrap transition-colors
        ${isActive
          ? "text-[hsl(var(--foreground))]"
          : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}
        ${className}`}
    >
      {label}
      {isActive && (
        sortDir === "asc"
          ? <ChevronUp className="w-3 h-3 inline ml-0.5" />
          : <ChevronDown className="w-3 h-3 inline ml-0.5" />
      )}
    </th>
  );
}
